import {
  ARTIFACTS_PATH,
  CONTROL_NOT_CAPTURED,
  type ControlReferenceResource,
  type LifecycleResource,
  type OutcomeEvidenceResource,
  type OutcomeFactResource,
  type OutcomeResource,
  type OutcomeVerdict,
  type RecordOutcomeRequest,
} from '@studio/shared';
import { useState, type ReactNode } from 'react';
import { api, ApiError } from '../../shell/api.js';
import { Failure } from '../../shell/EmptyState.js';
import { useNavigate, type Surface } from '../../shell/router.js';
import { useAsync } from '../../shell/useAsync.js';
import { assetTypeLabel, targetLabel } from '../../app/history.js';
import { materialName } from '../../app/material.js';
import { LearningCard } from './LearningCard.js';
import { useLifecycle } from './useLifecycle.js';

/**
 * Learn: the last stage, and the one that costs the least.
 *
 * Marking Live is one press and freezes what was running. Closing is one press, or two
 * when a Candidate won and has to be named. No number is required anywhere on this page,
 * and `Do this later` leaves without changing anything: an Experiment is allowed to stay
 * Exported or Live for as long as it stays true.
 */

const STATE_LABEL: Record<LifecycleResource['state'], string> = {
  exploring: 'Exploring',
  exported: 'Exported',
  live: 'Live',
  closed: 'Closed',
};

/** One vocabulary for the four verdicts on this surface. */
const VERDICT_LABEL: Record<OutcomeVerdict, string> = {
  candidateWon: 'Candidate победил',
  controlWon: 'Control победил',
  noClearWinner: 'Нет явного победителя',
  notCompleted: 'Не проведён / остановлен',
};

function day(moment: string | null): string {
  return moment === null ? 'ещё нет' : moment.slice(0, 10);
}

function Learn({ experimentId }: { experimentId: string }): ReactNode {
  const open = useLifecycle(experimentId);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const stage = open.stage;
  if (open.error !== null) {
    return (
      <section className="page">
        <p className="eyebrow">Learn</p>
        <Failure message={open.error} onRetry={() => open.refresh()} />
      </section>
    );
  }
  if (stage === null) return <section className="page" aria-busy="true" />;

  const run = async (work: () => Promise<LifecycleResource>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      open.apply(await work());
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Это не удалось записать.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="page learn">
      <div className="page-head">
        <div>
          <p className="eyebrow">{`Learn · ${STATE_LABEL[stage.state]}`}</p>
          <h1>{stage.hypothesis ?? 'Что проверял этот Experiment'}</h1>
          <p className="note">{`${assetTypeLabel(stage.assetType)} · ${targetLabel(stage.target)} · ${stage.appDisplayName}`}</p>
        </div>
        {stage.state === 'exploring' || stage.state === 'exported' ? (
          <div className="brief-launch">
            <button className="primary" type="button" disabled={busy} onClick={() => void run(() => api.markLive(experimentId))}>
              Mark Live
            </button>
            <p className="note">Фиксирует запуск и замораживает текущее как Control. Ничего не публикуется.</p>
          </div>
        ) : null}
      </div>

      {error === null ? null : <Failure message={error} />}

      <ol className="lifecycle-strip" aria-label="Жизненный цикл Experiment">
        <li>{`Создан ${day(stage.createdAt)}`}</li>
        <li>{`Первый Export ${day(stage.firstExportedAt)}`}</li>
        <li>{`Mark Live ${day(stage.markedLiveAt)}`}</li>
        <li>{`Closed ${day(stage.closedAt)}`}</li>
      </ol>

      <details className="secondary-action">
        <summary>Ввести исторический тест</summary>
        <p className="note">Даты пишутся отдельно. Только для теста, который был до Studio.</p>
        <Backdate stage={stage} busy={busy} onSubmit={(body) => void run(() => api.backdateLifecycle(experimentId, body))} />
      </details>

      <Control
        stage={stage}
        busy={busy}
        onReplace={(artifactIds) => void run(() => api.replaceControl(experimentId, { artifactIds: [...artifactIds] }))}
      />

      {stage.outcome === null ? (
        <Capture stage={stage} busy={busy} onClose={(body) => void run(() => api.recordOutcome(experimentId, body))} onLater={() => navigate(`/apps/${stage.appId}`)} />
      ) : (
        <Recorded experimentId={experimentId} outcome={stage.outcome} onEdited={() => open.refresh()} />
      )}

      {stage.learning === null ? null : <LearningCard learning={stage.learning} onChanged={() => open.refresh()} />}
    </section>
  );
}

/** The Control, as it stands. Its absence is stated and blocks nothing. */
function Control({
  stage,
  busy,
  onReplace,
}: {
  stage: LifecycleResource;
  busy: boolean;
  onReplace: (artifactIds: readonly string[]) => void;
}): ReactNode {
  const control: ControlReferenceResource | null = stage.control;
  const library = useAsync(async () => api.readLibrary(stage.appId), [stage.appId]);
  const [uploading, setUploading] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const usable = (library.value?.items ?? []).filter((item) => item.artifactId !== null);

  /**
   * R136, and the reason it is a file picker.
   *
   * The record is corrected with the exact asset Play Console was running, and that
   * asset is a file on the operator's machine. This surface used to ask for an Artifact
   * identifier typed by hand - and no screen in the Studio has ever shown one, so the
   * field could not be filled in by anyone. Bytes go through the Artifact seam first,
   * exactly as Outcome Evidence does two sections below.
   */
  const replaceWithFile = (file: File | undefined): void => {
    if (file === undefined) return;
    setUploading(true);
    setFailed(null);
    void api
      .uploadArtifact(file)
      .then((artifact) => onReplace([artifact.id]))
      .catch((caught: unknown) =>
        setFailed(caught instanceof ApiError ? caught.message : 'Эти байты не удалось сохранить.'),
      )
      .finally(() => setUploading(false));
  };

  return (
    <section className="control-panel" aria-label="Control">
      <h2>Control</h2>
      {control === null ? (
        <p className="note">{`${CONTROL_NOT_CAPTURED}. Здесь это ничего не блокирует.`}</p>
      ) : (
        <>
          <p className="note">
            {control.source === 'controlSnapshot'
              ? `Заморожен, когда этот Experiment стал Live${control.capturedAt === null ? '' : ` · ${control.capturedAt.slice(0, 10)}`}`
              : `Превью из последнего просмотренного Listing Snapshot${control.capturedAt === null ? '' : ` · ${control.capturedAt.slice(0, 10)}`} — не Control этого Experiment, пока он не станет Live`}
          </p>
          <ul className="control-items" aria-label="Ассеты Control">
            {control.items.map((item) => (
              <li key={item.position}>
                {item.artifactId === null ? (
                  <span className="note">{item.sourceUrl ?? 'Доставлено по URL'}</span>
                ) : (
                  <img className="frame" src={`${ARTIFACTS_PATH}/${item.artifactId}`} alt={`Control ${item.position + 1}`} loading="lazy" />
                )}
              </li>
            ))}
          </ul>
        </>
      )}
      <details className="secondary-action">
        <summary>Заменить Control</summary>
        <p className="note">Точный ассет, который крутился в Play Console. Прежняя запись сохраняется рядом.</p>
        <label htmlFor="control-upload">Ассет Play Console</label>
        <input
          id="control-upload"
          type="file"
          accept="image/*,video/*"
          disabled={busy || uploading}
          onChange={(event) => replaceWithFile(event.target.files?.[0])}
        />
        {usable.length === 0 ? null : (
          <>
            <label htmlFor="control-material" className="note">
              Или материал, который этот App уже помнит
            </label>
            <select
              id="control-material"
              value=""
              disabled={busy || uploading}
              onChange={(event) => {
                const item = usable.find((entry) => entry.id === event.target.value);
                if (item !== undefined && item.artifactId !== null) onReplace([item.artifactId]);
              }}
            >
              <option value="">Оставить Control как есть</option>
              {usable.map((item) => (
                <option key={item.id} value={item.id}>
                  {materialName(item)}
                </option>
              ))}
            </select>
          </>
        )}
        {failed === null ? null : <Failure message={failed} />}
      </details>
    </section>
  );
}

/**
 * Closing. Four buttons and nothing else required: the note, the evidence and every
 * number in it are optional, and only `Candidate won` also asks which Candidate.
 */
function Capture({
  stage,
  busy,
  onClose,
  onLater,
}: {
  stage: LifecycleResource;
  busy: boolean;
  onClose: (body: RecordOutcomeRequest) => void;
  onLater: () => void;
}): ReactNode {
  const [winner, setWinner] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [evidence, setEvidence] = useState('');
  const [needsWinner, setNeedsWinner] = useState(false);

  const record = (verdict: OutcomeVerdict): void => {
    if (verdict === 'candidateWon' && winner === null) {
      setNeedsWinner(true);
      return;
    }
    setNeedsWinner(false);
    onClose({
      verdict,
      ...(verdict === 'candidateWon' && winner !== null ? { winningCandidateId: winner } : {}),
      ...(note.trim() === '' ? {} : { note: note.trim() }),
      ...(evidence.trim() === '' ? {} : { evidence: [{ kind: 'text' as const, text: evidence.trim() }] }),
    });
  };

  return (
    <section className="outcome-capture" aria-label="Outcome">
      <h2>Как прошло?</h2>
      <p className="note">Один ответ закрывает этот Experiment. Число не обязательно, и ничего здесь заполнять не нужно.</p>

      {stage.candidates.length === 0 ? null : (
        <fieldset className="winner-picker">
          <legend>Candidate, который победил</legend>
          {stage.candidates.map((candidate) => (
            <label key={candidate.candidateId} className={candidate.candidateId === winner ? 'asset-choice active' : 'asset-choice'}>
              <input
                type="radio"
                name="winner"
                value={candidate.candidateId}
                checked={candidate.candidateId === winner}
                onChange={() => {
                  setWinner(candidate.candidateId);
                  setNeedsWinner(false);
                }}
              />
              {candidate.label}
            </label>
          ))}
        </fieldset>
      )}

      <div className="verdict-choices" role="group" aria-label="Outcome">
        {(Object.keys(VERDICT_LABEL) as OutcomeVerdict[]).map((verdict) => (
          <button key={verdict} type="button" className="asset-choice" disabled={busy} onClick={() => record(verdict)}>
            {VERDICT_LABEL[verdict]}
          </button>
        ))}
      </div>
      {needsWinner ? (
        <p className="failure" role="alert">
          Выберите Candidate, который победил.
        </p>
      ) : null}

      <details className="secondary-action">
        <summary>Добавить заметку или то, что сказал Play Console</summary>
        <label htmlFor="outcome-note">Заметка Outcome</label>
        <textarea id="outcome-note" rows={2} value={note} onChange={(event) => setNote(event.target.value)} />
        <label htmlFor="outcome-evidence">Вставьте, что сказал Play Console</label>
        <textarea id="outcome-evidence" rows={3} value={evidence} onChange={(event) => setEvidence(event.target.value)} />
        <p className="note">Числа считываются отсюда, где возможно, и остаются редактируемыми. Ни одно не обязательно.</p>
      </details>

      <button type="button" className="ghost" onClick={onLater}>
        Сделать позже
      </button>
    </section>
  );
}

/** What was recorded, once it was. Extracted numbers show as the facts they are. */
function Recorded({
  experimentId,
  outcome,
  onEdited,
}: {
  experimentId: string;
  outcome: OutcomeResource;
  onEdited: () => void;
}): ReactNode {
  return (
    <section className="outcome-capture" aria-label="Outcome">
      <h2>{VERDICT_LABEL[outcome.verdict]}</h2>
      {outcome.note === null ? null : <p className="note">{outcome.note}</p>}
      {outcome.evidence.length === 0 ? (
        <p className="note">Evidence не приложено. Outcome остаётся как есть.</p>
      ) : (
        <ul className="evidence-list" aria-label="Outcome Evidence">
          {outcome.evidence.map((piece) => (
            <li key={piece.id}>
              {piece.kind === 'text' ? (
                <blockquote>{piece.text}</blockquote>
              ) : (
                <img className="frame" src={`${ARTIFACTS_PATH}/${piece.artifactId ?? ''}`} alt="Скриншот Play Console" loading="lazy" />
              )}
              {/*
                Keyed by the version of the evidence it belongs to.

                Extraction is Job work that lands after the paste, and this component
                seeds its editable copy of the numbers once. Without the key the numbers
                the Studio read were on the resource, on the Creative Learning beside it
                and nowhere on this panel until the page was reloaded by hand - which is
                the same as not having read them.
              */}
              <Facts key={`${piece.id}:${piece.version}`} piece={piece} onEdited={onEdited} />
            </li>
          ))}
        </ul>
      )}
      <AttachEvidence experimentId={experimentId} onAttached={onEdited} />
    </section>
  );
}

/**
 * R143, and it is two ways in rather than one.
 *
 * What Play Console shows is easier to keep than to retype, so the original goes in as
 * it was: pasted text, or the screenshot itself dropped on the page or chosen from the
 * file picker beside it. Numbers are read out of whichever arrives and stay editable;
 * none of them is required and nothing here blocks the Outcome, which is already
 * recorded by the time this is on screen.
 */
function AttachEvidence({
  experimentId,
  onAttached,
}: {
  experimentId: string;
  onAttached: () => void;
}): ReactNode {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [over, setOver] = useState(false);

  const attach = async (work: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    setFailed(null);
    try {
      await work();
      setText('');
      onAttached();
    } catch (caught) {
      setFailed(caught instanceof ApiError ? caught.message : 'Это evidence не удалось приложить.');
    } finally {
      setBusy(false);
    }
  };

  /** Bytes go through the Artifact seam first; the Outcome then names what was stored. */
  const attachFile = (file: File | undefined): void => {
    if (file === undefined) return;
    void attach(async () => {
      const artifact = await api.uploadArtifact(file);
      return api.addOutcomeEvidence(experimentId, { kind: 'screenshot', artifactId: artifact.id });
    });
  };

  return (
    <details className="secondary-action">
      <summary>Приложить ещё evidence</summary>
      <label htmlFor="more-evidence">Вставьте, что сказал Play Console</label>
      <textarea id="more-evidence" rows={3} value={text} onChange={(event) => setText(event.target.value)} />
      <button
        type="button"
        className="ghost"
        disabled={busy || text.trim() === ''}
        onClick={() => void attach(() => api.addOutcomeEvidence(experimentId, { kind: 'text', text: text.trim() }))}
      >
        Приложить этот текст
      </button>

      <div
        className={over ? 'evidence-drop over' : 'evidence-drop'}
        role="group"
        aria-label="Evidence-скриншот"
        onDragOver={(event) => {
          event.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setOver(false);
          attachFile(event.dataTransfer.files[0]);
        }}
      >
        <p className="note">Перетащите сюда скриншот Play Console или выберите файл.</p>
        <label htmlFor="evidence-screenshot">Скриншот Play Console</label>
        <input
          id="evidence-screenshot"
          type="file"
          accept="image/*"
          disabled={busy}
          onChange={(event) => attachFile(event.target.files?.[0])}
        />
      </div>

      {failed === null ? null : <Failure message={failed} />}
    </details>
  );
}

/**
 * The numbers, and who they belong to.
 *
 * Whatever extraction managed to read is a starting point and nothing more: every value
 * is editable here, a number it missed can be typed in, and what an operator saves is
 * marked as theirs. The captured text or screenshot above is never touched by any of it -
 * the schema refuses that outright - so correcting a number cannot quietly rewrite the
 * evidence it came from.
 */
interface FactRow {
  /** Stable for as long as the row is on screen, so typing a name never remounts a field. */
  readonly rowId: string;
  /** What the fact is called on the resource, or null while the operator is naming it. */
  readonly key: string | null;
  readonly label: string;
  readonly value: string;
  readonly unit: string | null;
  /** Extraction is convenience; an operator who changes a value becomes its author. */
  readonly source: OutcomeFactResource['source'];
}

/**
 * The names the rest of the Studio already reads a number by.
 *
 * A history card answers `by how much` with the `uplift` key and nothing else, so a
 * number typed in as `Uplift` has to arrive under that key or it never reaches the card
 * R025 asks for. Everything else keeps the operator's own wording as its name.
 */
const NAMED_NUMBERS: Readonly<Record<string, { readonly key: string; readonly label: string; readonly unit: string | null }>> = {
  uplift: { key: 'uplift', label: 'Uplift', unit: '%' },
  lift: { key: 'uplift', label: 'Uplift', unit: '%' },
  conversion: { key: 'conversion', label: 'Конверсия', unit: '%' },
  'conversion rate': { key: 'conversion', label: 'Конверсия', unit: '%' },
  конверсия: { key: 'conversion', label: 'Конверсия', unit: '%' },
  confidence: { key: 'confidence', label: 'Уверенность', unit: '%' },
  уверенность: { key: 'confidence', label: 'Уверенность', unit: '%' },
  installs: { key: 'installs', label: 'Установки', unit: null },
  установки: { key: 'installs', label: 'Установки', unit: null },
};

function named(label: string, taken: readonly string[]): { key: string; label: string; unit: string | null } {
  const known = NAMED_NUMBERS[label.trim().toLowerCase()];
  if (known !== undefined && !taken.includes(known.key)) return { ...known };
  const slug = label.trim().toLowerCase().replace(/[^a-z0-9а-яё]+/gu, '-').replace(/^-|-$/gu, '');
  const base = slug === '' ? 'number' : slug;
  let key = base;
  for (let suffix = 2; taken.includes(key); suffix += 1) key = `${base}-${suffix}`;
  return { key, label: label.trim() === '' ? 'Число' : label.trim(), unit: known?.unit ?? null };
}

function rowsOf(facts: readonly OutcomeFactResource[]): readonly FactRow[] {
  return facts.map((fact) => ({
    rowId: fact.key,
    key: fact.key,
    label: fact.label,
    value: fact.value,
    unit: fact.unit,
    source: fact.source,
  }));
}

/** What leaves for the server. A row the operator named settles into a key here. */
function factsOf(rows: readonly FactRow[]): readonly OutcomeFactResource[] {
  const taken: string[] = [];
  const facts: OutcomeFactResource[] = [];
  for (const row of rows) {
    if (row.value.trim() === '') continue;
    const settled = row.key === null ? named(row.label, taken) : { key: row.key, label: row.label, unit: row.unit };
    taken.push(settled.key);
    facts.push({ ...settled, value: row.value, source: row.source });
  }
  return facts;
}

function Facts({ piece, onEdited }: { piece: OutcomeEvidenceResource; onEdited: () => void }): ReactNode {
  const [rows, setRows] = useState<readonly FactRow[]>(() => rowsOf(piece.facts));
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  const change = (rowId: string, patch: Partial<FactRow>): void => {
    setRows((held) => held.map((row) => (row.rowId === rowId ? { ...row, ...patch } : row)));
    setDirty(true);
  };

  const save = async (): Promise<void> => {
    setBusy(true);
    try {
      await api.updateOutcomeEvidence(piece.id, factsOf(rows));
      setDirty(false);
      onEdited();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fact-list">
      {rows.length === 0 ? (
        <p className="note">
          {piece.extractionState === 'unavailable'
            ? 'Сохранено как было. Числа из него не считались.'
            : 'Числа из этого не считались. Ни одно не обязательно.'}
        </p>
      ) : null}
      {rows.map((row) => (
        <p key={row.rowId}>
          {row.key === null ? (
            <>
              <label htmlFor={`fact-name-${piece.id}-${row.rowId}`}>Что это за число</label>
              <input
                id={`fact-name-${piece.id}-${row.rowId}`}
                value={row.label}
                placeholder="Uplift"
                onChange={(event) => change(row.rowId, { label: event.target.value })}
              />
            </>
          ) : null}
          <label htmlFor={`fact-${piece.id}-${row.rowId}`}>
            {`${row.label.trim() === '' ? 'Число' : row.label}${row.unit === null ? '' : ` (${row.unit})`}`}
          </label>
          <input
            id={`fact-${piece.id}-${row.rowId}`}
            value={row.value}
            onChange={(event) => change(row.rowId, { value: event.target.value, source: 'operator' })}
          />
        </p>
      ))}
      <button
        type="button"
        className="ghost"
        disabled={busy}
        onClick={() => {
          setRows((held) => [
            ...held,
            { rowId: `added-${held.length + 1}`, key: null, label: '', value: '', unit: null, source: 'operator' as const },
          ]);
          setDirty(true);
        }}
      >
        Добавить число
      </button>
      <button type="button" className="ghost" disabled={busy || !dirty} onClick={() => void save()}>
        Сохранить эти числа
      </button>
    </div>
  );
}

/** Secondary by design: the ordinary flow never shows a date field at all. */
function Backdate({
  stage,
  busy,
  onSubmit,
}: {
  stage: LifecycleResource;
  busy: boolean;
  onSubmit: (body: { markedLiveAt?: string; closedAt?: string }) => void;
}): ReactNode {
  const [liveOn, setLiveOn] = useState(stage.markedLiveAt?.slice(0, 10) ?? '');
  const [closedOn, setClosedOn] = useState(stage.closedAt?.slice(0, 10) ?? '');

  return (
    <div className="backdate">
      <label htmlFor="backdate-live">Mark Live</label>
      <input id="backdate-live" type="date" value={liveOn} onChange={(event) => setLiveOn(event.target.value)} />
      <label htmlFor="backdate-closed">Closed</label>
      <input id="backdate-closed" type="date" value={closedOn} onChange={(event) => setClosedOn(event.target.value)} />
      <button
        type="button"
        className="ghost"
        disabled={busy || (liveOn === '' && closedOn === '')}
        onClick={() =>
          onSubmit({
            ...(liveOn === '' ? {} : { markedLiveAt: new Date(`${liveOn}T12:00:00Z`).toISOString() }),
            ...(closedOn === '' ? {} : { closedAt: new Date(`${closedOn}T12:00:00Z`).toISOString() }),
          })
        }
      >
        Записать эти даты
      </button>
    </div>
  );
}

export const surface: Surface = {
  id: 'experiment-learn',
  path: '/experiments/:experimentId/learn',
  title: 'Learn',
  render: (params) => <Learn experimentId={params.experimentId ?? ''} />,
};
